import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

/**
 * Daily Balance Calculator
 * 
 * Reconstructs daily balances from ledger events and calculates monthly performance
 * considering deposits (D+1 start) and withdrawals (D0 stop).
 * 
 * @param {Object} params
 * @param {number} params.openingBalance - Balance at start of month
 * @param {Array} params.ledgers - User ledger entries for the month
 * @param {Array} params.dailyPercentages - Daily fund percentages [0.01, 0.02, ...]
 * @param {dayjs} params.monthStart - First day of month
 * @param {dayjs} params.monthEnd - Last day of month
 * @param {string} params.userId - User ID for logging
 * @param {number} params.commissionRate - Commission rate (%) for positive performance (optional, default: 30)
 * 
 * @returns {Object} {
 *   dailyBalances: Array of {date, balance, profit, events},
 *   totalGrossProfit: number,
 *   commission: number,
 *   netProfit: number,
 *   finalBalance: number,
 *   grossPerformancePercentage: number,
 *   netPerformancePercentage: number
 * }
 */
export function calculateDailyBalance({
  openingBalance,
  ledgers = [],
  dailyPercentages = [],
  monthStart,
  monthEnd,
  userId = 'unknown',
  commissionRate = null // Optional: if provided, use it; otherwise default to 30%
}) {
  // Validate inputs
  if (!monthStart || !monthEnd) {
    throw new Error('monthStart and monthEnd are required');
  }

  const startDate = dayjs(monthStart).startOf('day');
  const endDate = dayjs(monthEnd).endOf('day');
  const daysInMonth = endDate.diff(startDate, 'day') + 1;

  // Normalize daily percentages array
  const percentages = Array.isArray(dailyPercentages) && dailyPercentages.length > 0
    ? dailyPercentages
    : Array(daysInMonth).fill(0);

  // Ensure we have percentage for each day
  while (percentages.length < daysInMonth) {
    percentages.push(0);
  }

  // Parse and categorize ledger events
  const events = parseLedgerEvents(ledgers, startDate, endDate);

  // Calculate daily balances
  const dailyBalances = [];
  let currentBalance = parseFloat(openingBalance) || 0;
  let totalGrossProfit = 0;

  for (let dayIndex = 0; dayIndex < daysInMonth; dayIndex++) {
    const currentDate = startDate.add(dayIndex, 'day');
    const dayPercentage = parseFloat(percentages[dayIndex]) || 0;

    // Apply events from previous day (D-1) that start trading today
    const depositsToday = events.deposits.filter(e => 
      dayjs(e.tradingStartDate).isSame(currentDate, 'day')
    );

    // Apply withdrawals that stop trading today (D0)
    const withdrawalsToday = events.withdrawals.filter(e =>
      dayjs(e.tradingStopDate).isSame(currentDate, 'day')
    );

    // Update balance with deposits (from yesterday's approvals)
    depositsToday.forEach(deposit => {
      currentBalance += deposit.amount;
    });

    // Calculate today's profit on current balance
    const dailyProfit = (currentBalance * dayPercentage) / 100;
    totalGrossProfit += dailyProfit;

    // Apply withdrawals (stop trading immediately)
    withdrawalsToday.forEach(withdrawal => {
      currentBalance -= withdrawal.amount;
    });

    dailyBalances.push({
      date: currentDate.format('YYYY-MM-DD'),
      dayNumber: dayIndex + 1,
      openingBalance: currentBalance - dailyProfit + withdrawalsToday.reduce((sum, w) => sum + w.amount, 0) - depositsToday.reduce((sum, d) => sum + d.amount, 0),
      deposits: depositsToday,
      withdrawals: withdrawalsToday,
      percentage: dayPercentage,
      profit: dailyProfit,
      closingBalance: currentBalance,
    });
  }

  // Calculate commission (use provided rate or default to 30% if positive, 0% if negative)
  const shouldApplyCommission = totalGrossProfit >= 0;
  const finalCommissionRate = commissionRate !== null 
    ? (shouldApplyCommission ? commissionRate : 0)
    : (shouldApplyCommission ? 30 : 0); // Default fallback if not provided
  const commission = (totalGrossProfit * finalCommissionRate) / 100;
  const netProfit = totalGrossProfit - commission;
  const finalBalance = openingBalance + netProfit;

  // Calculate percentages
  const grossPerformancePercentage = openingBalance !== 0
    ? (totalGrossProfit * 100) / openingBalance
    : 0;

  const netPerformancePercentage = openingBalance !== 0
    ? (netProfit * 100) / openingBalance
    : 0;

  return {
    dailyBalances,
    totalGrossProfit,
    commission,
    commissionRate: finalCommissionRate,
    netProfit,
    finalBalance,
    grossPerformancePercentage,
    netPerformancePercentage,
    openingBalance,
    summary: {
      userId,
      month: startDate.format('MMMM YYYY'),
      daysInMonth,
      depositsCount: events.deposits.length,
      withdrawalsCount: events.withdrawals.length,
      totalDeposited: events.deposits.reduce((sum, d) => sum + d.amount, 0),
      totalWithdrawn: events.withdrawals.reduce((sum, w) => sum + w.amount, 0),
    }
  };
}

/**
 * Parse ledger events and categorize by type
 * 
 * @param {Array} ledgers - Raw ledger entries
 * @param {dayjs} monthStart - Start of month
 * @param {dayjs} monthEnd - End of month
 * @returns {Object} { deposits: [], withdrawals: [], performances: [] }
 */
function parseLedgerEvents(ledgers, monthStart, monthEnd) {
  const deposits = [];
  const withdrawals = [];
  const performances = [];

  ledgers.forEach(ledger => {
    const ledgerDate = ledger.date?.toDate?.() 
      ? dayjs(ledger.date.toDate()) 
      : dayjs(ledger.date);

    // Only consider events within or affecting the month
    if (!ledgerDate.isValid()) return;

    const transectionType = ledger.transection_type;

    // Type 0: Deposit (starts trading D+1)
    if (transectionType === 0) {
      const amount = parseFloat(ledger.new_txn_amount || ledger.total_amount || 0);
      if (amount > 0) {
        deposits.push({
          date: ledgerDate.format('YYYY-MM-DD'),
          approvalDate: ledgerDate,
          tradingStartDate: ledgerDate.add(1, 'day'), // D+1
          amount,
          docId: ledger.doc_id,
          type: 'deposit'
        });
      }
    }

    // Type 2: Withdrawal (stops trading D0 when status becomes 1)
    if (transectionType === 2) {
      const amount = parseFloat(ledger.new_txn_amount || ledger.withdraw_amount || 0);
      if (amount > 0) {
        withdrawals.push({
          date: ledgerDate.format('YYYY-MM-DD'),
          requestDate: ledgerDate,
          tradingStopDate: ledgerDate, // D0 - stops immediately
          amount,
          docId: ledger.doc_id,
          type: 'withdrawal'
        });
      }
    }

    // Type 3: Performance (already processed monthly closing)
    if (transectionType === 3) {
      performances.push({
        date: ledgerDate.format('YYYY-MM-DD'),
        amount: parseFloat(ledger.profit_loss_balance || ledger.new_txn_amount || 0),
        percentage: parseFloat(ledger.profit_loss_percent || 0),
        docId: ledger.doc_id,
        type: 'performance'
      });
    }
  });

  return {
    deposits: deposits.sort((a, b) => dayjs(a.approvalDate).diff(dayjs(b.approvalDate))),
    withdrawals: withdrawals.sort((a, b) => dayjs(a.requestDate).diff(dayjs(b.requestDate))),
    performances
  };
}

/**
 * Get events summary for logging/debugging
 * 
 * @param {Array} dailyBalances - Daily balance array from calculator
 * @returns {string} Formatted summary text
 */
export function formatDailyBalanceSummary(result) {
  if (!result || !result.dailyBalances) return '';

  let summary = `\n=== Daily Balance Summary ===\n`;
  summary += `User: ${result.summary.userId}\n`;
  summary += `Month: ${result.summary.month}\n`;
  summary += `Opening Balance: $${result.openingBalance.toFixed(2)}\n`;
  summary += `Deposits: ${result.summary.depositsCount} transactions ($${result.summary.totalDeposited.toFixed(2)})\n`;
  summary += `Withdrawals: ${result.summary.withdrawalsCount} transactions ($${result.summary.totalWithdrawn.toFixed(2)})\n\n`;

  summary += `Day | Balance | % | Profit | Events\n`;
  summary += `${'='.repeat(60)}\n`;

  result.dailyBalances.forEach(day => {
    const events = [];
    if (day.deposits.length > 0) {
      events.push(`+$${day.deposits.reduce((sum, d) => sum + d.amount, 0).toFixed(2)} deposit`);
    }
    if (day.withdrawals.length > 0) {
      events.push(`-$${day.withdrawals.reduce((sum, w) => sum + w.amount, 0).toFixed(2)} withdrawal`);
    }

    summary += `${day.dayNumber.toString().padStart(2)} | $${day.closingBalance.toFixed(2).padStart(12)} | ${day.percentage.toFixed(4)}% | $${day.profit.toFixed(2).padStart(8)} | ${events.join(', ') || '-'}\n`;
  });

  summary += `\n`;
  summary += `Gross Profit: $${result.totalGrossProfit.toFixed(2)} (${result.grossPerformancePercentage.toFixed(4)}%)\n`;
  summary += `Commission (${result.commissionRate}%): -$${result.commission.toFixed(2)}\n`;
  summary += `Net Profit: $${result.netProfit.toFixed(2)} (${result.netPerformancePercentage.toFixed(4)}%)\n`;
  summary += `Final Balance: $${result.finalBalance.toFixed(2)}\n`;
  summary += `${'='.repeat(60)}\n`;

  return summary;
}

export default calculateDailyBalance;

